// Тонкая типизированная обёртка над официальным window.Telegram.WebApp (telegram-web-app.js).
// Единственное место, которое трогает глобальный объект Telegram. Описаны только используемые поля.

type Inset = { top: number; bottom: number; left: number; right: number };
type EventName = 'themeChanged' | 'viewportChanged' | 'safeAreaChanged' | 'contentSafeAreaChanged'
  | 'backButtonClicked' | 'fullscreenChanged' | 'fullscreenFailed' | 'activated' | 'deactivated'
  | 'homeScreenChecked' | 'homeScreenAdded';

/** Ярлык Mini App на главном экране телефона (Bot API 8.0): added — уже есть, missed — можно добавить, unsupported —
 *  клиент не умеет, unknown — клиент не смог узнать. */
export type HomeScreenStatus = 'unsupported' | 'unknown' | 'added' | 'missed';

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
}

interface WebApp {
  initData: string;
  initDataUnsafe: { user?: TelegramUser; start_param?: string; auth_date?: number };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string | undefined>;
  isExpanded: boolean;
  isFullscreen?: boolean;
  safeAreaInset?: Inset;
  contentSafeAreaInset?: Inset;
  isVersionAtLeast(version: string): boolean;
  ready(): void;
  expand(): void;
  disableVerticalSwipes?(): void;
  requestFullscreen?(): void;
  shareMessage?(msgId: string, callback?: (sent: boolean) => void): void;
  openInvoice?(url: string, callback?: (status: InvoiceStatus) => void): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  setBottomBarColor?(color: string): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  checkHomeScreenStatus?(callback?: (status: HomeScreenStatus) => void): void;
  addToHomeScreen?(): void;
  onEvent(event: EventName, handler: (params?: unknown) => void): void;
  offEvent(event: EventName, handler: (params?: unknown) => void): void;
  BackButton: { isVisible: boolean; show(): void; hide(): void;
    onClick(cb: () => void): void; offClick(cb: () => void): void };
  HapticFeedback: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
}

declare global {
  interface Window { Telegram?: { WebApp?: WebApp }; __TG_MOCK__?: boolean;
    /** dev-mock: статус ярлыка на главном экране (?home=missed|added|unsupported|unknown, dev/telegramMock.ts). */
    __TG_MOCK_HOME__?: HomeScreenStatus }
}

const webApp: WebApp | undefined = window.Telegram?.WebApp;

/** Открыто внутри Telegram (или dev-mock подставил launch-параметры). */
export const isTelegram = Boolean(webApp?.initData);
/** Dev-режим: параметры подставлены плагином telegramMock, Telegram-клиента нет. В сборке — литерал false (мока там нет,
 *  и имя его флага в бандл не попадает). */
export const isMock = import.meta.env.DEV && Boolean(window.__TG_MOCK__);
/** Клиент Telegram: android, ios, tdesktop, macos, weba, webk…; вне Telegram — web. */
export const telegramPlatform = (): string => (isTelegram && webApp?.platform) || 'web';

export function tg(): WebApp | undefined {
  return webApp;
}

/** Сырая строка initData — отдаётся серверу как есть; клиент ей не доверяет. */
export function rawInitData(): string {
  return webApp?.initData ?? '';
}

export function telegramUser(): TelegramUser | undefined {
  return webApp?.initDataUnsafe.user;
}

export function startParam(): string | undefined {
  // start_param едет в initData; на некоторых клиентах — ещё и в query tgWebAppStartParam. Кнопки web_app
  // (клавиатура бота) start_param не передают — раздел едет в нашем query ?startapp= адреса кнопки.
  const query = new URLSearchParams(location.search);
  return webApp?.initDataUnsafe.start_param
    ?? query.get('tgWebAppStartParam') ?? query.get('startapp') ?? undefined;
}

function supports(version: string): boolean {
  return Boolean(webApp?.isVersionAtLeast(version)) && !isMock;
}

/** Цвета темы Telegram → CSS-переменные --tg-* на <html>. Схему приложения (data-scheme) ставит src/theme/theme.ts. */
function applyThemeParams(): void {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(webApp?.themeParams ?? {})) {
    if (value) root.style.setProperty(`--tg-${key.replace(/_/g, '-')}`, value);
  }
}

/** Схема Telegram (colorScheme); вне Telegram — системная схема браузера. */
export function telegramColorScheme(): 'light' | 'dark' {
  return webApp?.colorScheme ?? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
}

/** Шапка, фон и нижняя панель Telegram — цветом фона приложения, чтобы не было шва (Bot API 6.1 / 7.10). */
export function setChromeColor(color: string): void {
  if (!color || !webApp) return;
  if (supports('6.1')) {
    webApp.setHeaderColor(color);
    webApp.setBackgroundColor(color);
  }
  if (supports('7.10')) webApp.setBottomBarColor?.(color);
}

/** Telegram сменил тему (themeChanged): параметры темы уже применены, cb — пересчитать схему приложения. */
export function onThemeChanged(cb: () => void): () => void {
  if (!webApp) return () => {};
  webApp.onEvent('themeChanged', cb);
  return () => webApp.offEvent('themeChanged', cb);
}

function applyInsets(): void {
  const root = document.documentElement;
  const set = (prefix: string, inset?: Inset) => {
    for (const side of ['top', 'bottom', 'left', 'right'] as const) {
      root.style.setProperty(`--${prefix}-${side}`, `${inset?.[side] ?? 0}px`);
    }
  };
  set('tg-safe', webApp?.safeAreaInset);
  set('tg-content-safe', webApp?.contentSafeAreaInset);
}

const MOBILE = new Set(['android', 'android_x', 'ios']);

/** Телефон: полный экран (requestFullscreen) только здесь — на десктопе он растянул бы окно на весь монитор. */
export const isMobilePlatform = (platform: string | undefined) => MOBILE.has(platform ?? '');

export function initTelegram(): void {
  applyThemeParams();
  applyInsets();
  if (!webApp) return;
  webApp.onEvent('themeChanged', applyThemeParams);
  webApp.onEvent('safeAreaChanged', applyInsets);
  webApp.onEvent('contentSafeAreaChanged', applyInsets);
  webApp.onEvent('fullscreenChanged', applyInsets);
  webApp.ready();
  webApp.expand();
  // Вертикальный свайп сворачивает Mini App и мешает прокрутке длинных списков.
  if (supports('7.7')) webApp.disableVerticalSwipes?.();
  // Полный экран (решение владельца 08.10) — только на телефонах: на десктопе он растянул бы окно на весь монитор.
  // Кнопки Telegram (закрыть, ⋮) остаются поверх — их полосу даёт contentSafeAreaInset, вёрстка отступает на неё.
  if (supports('8.0') && isMobilePlatform(webApp.platform) && !webApp.isFullscreen) webApp.requestFullscreen?.();
}

/** Mini App ушла в фон / вернулась (Bot API 8.0). Нужна там, где visibilitychange не приходит (десктоп-клиенты). */
export function onActivityChange(cb: (active: boolean) => void): () => void {
  if (!webApp || !supports('8.0')) return () => {};
  const on = () => cb(true);
  const off = () => cb(false);
  webApp.onEvent('activated', on);
  webApp.onEvent('deactivated', off);
  return () => { webApp.offEvent('activated', on); webApp.offEvent('deactivated', off); };
}

export const haptic = {
  selection(): void { if (supports('6.1')) webApp?.HapticFeedback.selectionChanged(); },
  impact(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light'): void { if (supports('6.1')) webApp?.HapticFeedback.impactOccurred(style); },
};

/** Открытые окна поверх экрана (навыки, режимы): «Назад» закрывает верхнее из них, а не уводит с экрана. */
const overlays: Array<() => void> = [];
let routeBackVisible = false;

/** Системная кнопка «Назад»: показываем, пока есть куда вернуться (или пока открыто окно поверх экрана). */
export function bindBackButton(visible: boolean, onBack: () => void): () => void {
  if (!webApp || !supports('6.1')) return () => {};
  routeBackVisible = visible;
  if (visible || overlays.length) webApp.BackButton.show(); else webApp.BackButton.hide();
  const handler = () => { const top = overlays[overlays.length - 1]; if (top) top(); else onBack(); };
  webApp.BackButton.onClick(handler);
  return () => webApp.BackButton.offClick(handler);
}

/** Окно поверх экрана (12.10, слово владельца: «назад — нативно»): пока открыто, системная «Назад» видна и закрывает
 *  его; закрылось — кнопка как была у экрана. */
export function bindOverlayBack(onClose: () => void): () => void {
  if (!webApp || !supports('6.1')) return () => {};
  overlays.push(onClose);
  webApp.BackButton.show();
  return () => {
    const i = overlays.lastIndexOf(onClose);
    if (i >= 0) overlays.splice(i, 1);
    if (!overlays.length && !routeBackVisible) webApp.BackButton.hide();
  };
}

/** Поделиться: штатный выбор чата Telegram (t.me/share); вне Telegram — новая вкладка. */
export function shareToTelegram(url: string, text: string): void {
  const link = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  if (webApp && !isMock) webApp.openTelegramLink(link); else window.open(link, '_blank', 'noopener');
}

/** Готовое сообщение (savePreparedInlineMessage на сервере) → штатный выбор чата Telegram (Bot API 8.0). */
export function canShareMessage(): boolean {
  return Boolean(webApp?.shareMessage) && supports('8.0');
}
export function shareMessage(id: string): Promise<boolean> {
  return new Promise((resolve) => webApp!.shareMessage!(id, (sent) => resolve(sent)));
}

export function openExternal(url: string): void {
  if (webApp && !isMock) webApp.openLink(url); else window.open(url, '_blank', 'noopener');
}

/** Итог окна оплаты Telegram: paid — оплачено (начисляет сервер по вебхуку бота), pending — платёж в обработке. */
export type InvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending';

/** Счёт в Stars (createInvoiceLink на сервере) → штатное окно оплаты Telegram (Bot API 6.1). Вне Telegram и в dev-моке
 *  платить негде — «cancelled», покупка не происходит. */
export function canPay(): boolean {
  return Boolean(webApp?.openInvoice) && !isMock && supports('6.1');
}
export function openInvoice(url: string): Promise<InvoiceStatus> {
  if (!canPay()) return Promise.resolve('cancelled');
  return new Promise((resolve) => webApp!.openInvoice!(url, (status) => resolve(status)));
}

/** Ссылка t.me (приглашение, добавить бота в группу) — штатно внутри Telegram; вне Telegram — новая вкладка. */
export function openTelegramUrl(url: string): void {
  if (webApp && !isMock) webApp.openTelegramLink(url); else window.open(url, '_blank', 'noopener');
}

/** Ярлык на главном экране умеют клиенты с Bot API 8.0. В dev-моке Telegram-клиента нет — статус из ?home= адреса. */
export function canAddToHomeScreen(): boolean {
  if (isMock) return Boolean(window.__TG_MOCK_HOME__) && window.__TG_MOCK_HOME__ !== 'unsupported';
  return typeof webApp?.checkHomeScreenStatus === 'function' && typeof webApp.addToHomeScreen === 'function' && supports('8.0');
}

/** Статус ярлыка (homeScreenChecked). Не умеет клиент или вызов бросил — unsupported. */
export function checkHomeScreenStatus(cb: (status: HomeScreenStatus) => void): void {
  if (!canAddToHomeScreen()) { cb('unsupported'); return; }
  if (isMock) { const status = window.__TG_MOCK_HOME__!; setTimeout(() => cb(status), 0); return; }
  try { webApp!.checkHomeScreenStatus!((status) => cb(status)); } catch { cb('unsupported'); }
}

const mockHomeAdded = new Set<() => void>();

/** Штатное окно Telegram «Добавить на главный экран»; согласился человек — придёт homeScreenAdded. */
export function addToHomeScreen(): void {
  if (!canAddToHomeScreen()) return;
  if (isMock) { window.__TG_MOCK_HOME__ = 'added'; mockHomeAdded.forEach((cb) => cb()); return; }
  try { webApp!.addToHomeScreen!(); } catch { /* клиент отказал — ярлыка нет, приложение живёт дальше */ }
}

/** Ярлык добавлен (событие homeScreenAdded). Возвращает отписку. */
export function onHomeScreenAdded(cb: () => void): () => void {
  if (!canAddToHomeScreen()) return () => {};
  if (isMock) { mockHomeAdded.add(cb); return () => { mockHomeAdded.delete(cb); }; }
  const handler = () => cb();
  webApp!.onEvent('homeScreenAdded', handler);
  return () => webApp!.offEvent('homeScreenAdded', handler);
}
